import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Control, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { api } from "../api/client";
import { queryKeys } from "../api/queryKeys";
import { ComboBoxField, type ComboBoxOption, TextField } from "./form";

/** Valeurs communes aux formulaires de création et d'édition d'un compte. */
export type AccountFormValues = {
  login: string;
  nom: string;
  /**
   * Id de la société dans le référentiel MAÎTRE (dbo.Entreprise de DB_CRACQ), tel que
   * renvoyé par la recherche. "" tant qu'aucune société n'est choisie.
   */
  idEntreprise: number | "";
  /**
   * Nom de la société choisie. Soumis avec le formulaire car le serveur en a besoin pour
   * créer la ligne locale dbo.Companies si la société n'y figure pas encore.
   */
  nomEntreprise: string;
};

/** Seuil imposé par Milo, et déjà celui d'Ikki sur ce même référentiel. */
export const COMPANY_MIN_CHARS = 3;

/**
 * Recherche de sociétés, déléguée à Milo (seul à accéder à DB_CRACQ).
 *
 * Anti-rebond de 300 ms : sans lui, chaque frappe déclencherait un appel réseau. Rien
 * n'est demandé sous le seuil de caractères, ce qui évite un aller-retour que Milo
 * refuserait de toute façon.
 */
export function useCompanySearch() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const enabled = debounced.length >= COMPANY_MIN_CHARS;

  const search = useQuery({
    queryKey: queryKeys.companySearch(debounced),
    queryFn: () => api.referentials.companies(debounced),
    enabled,
    staleTime: 60 * 1000,
  });

  const options = useMemo<ComboBoxOption[]>(
    () => (search.data ?? []).map((c) => ({ value: c.idEntreprise, label: c.nomEntreprise })),
    [search.data],
  );

  return {
    setQuery,
    options,
    loading: enabled && search.isFetching,
    error: search.error as Error | null,
  };
}

type Props = {
  control: Control<AccountFormValues>;
  getValues: UseFormGetValues<AccountFormValues>;
  setValue: UseFormSetValue<AccountFormValues>;
  autoFocusLogin?: boolean;
  /**
   * Société déjà rattachée au compte, en édition : affichée d'emblée dans la liste pour
   * que le champ montre son nom sans qu'on ait à relancer une recherche.
   */
  initialCompany?: ComboBoxOption;
};

/**
 * Les trois champs identitaires d'un compte, partagés par la création et l'édition pour
 * qu'ils ne divergent pas.
 *
 * L'entreprise est placée AVANT le nom : choisir l'entreprise renseigne le nom quand il
 * est encore vide, l'ordre suit donc le geste de saisie. Une valeur déjà saisie n'est
 * jamais écrasée.
 *
 * La recherche porte sur le référentiel MAÎTRE des sociétés (dbo.Entreprise de DB_CRACQ),
 * interrogé via Milo : wapagri.Entreprise a été constatée non à jour et abandonnée le
 * 09/09/2026. Le serveur retrouve ou crée ensuite la ligne locale dbo.Companies, comme
 * le fait Ikki.
 */
export function AccountFormFields({
  control, getValues, setValue, autoFocusLogin, initialCompany,
}: Props) {
  const { setQuery, options, loading, error } = useCompanySearch();

  // La société courante reste proposable tant que l'utilisateur n'a pas cherché autre
  // chose, sinon le champ afficherait un libellé vide en ouvrant le formulaire d'édition.
  const withInitial = useMemo(() => {
    if (!initialCompany) return options;
    if (options.some((o) => o.value === initialCompany.value)) return options;
    return [initialCompany, ...options];
  }, [options, initialCompany]);

  return (
    <>
      <TextField
        control={control}
        name="login"
        label="Login"
        autoFocus={autoFocusLogin}
        rules={{ required: "Login obligatoire", maxLength: { value: 50, message: "50 caractères maximum" } }}
      />
      <ComboBoxField
        control={control}
        name="idEntreprise"
        label="Société"
        placeholder={`Taper au moins ${COMPANY_MIN_CHARS} caractères...`}
        options={withInitial}
        loading={loading}
        minChars={COMPANY_MIN_CHARS}
        message={error ? "Recherche indisponible : Milo n'a pas répondu." : undefined}
        onQueryChange={setQuery}
        rules={{ required: "Société obligatoire" }}
        onPick={(option) => {
          // Le nom de la société part avec le formulaire : le serveur en a besoin pour
          // créer la ligne locale si elle n'existe pas.
          setValue("nomEntreprise", option.label, { shouldDirty: true });
          if (!getValues("nom").trim())
            setValue("nom", option.label, { shouldValidate: true, shouldDirty: true });
        }}
      />
      <TextField
        control={control}
        name="nom"
        label="Nom (client / société)"
        rules={{ required: "Nom obligatoire", maxLength: { value: 100, message: "100 caractères maximum" } }}
      />
    </>
  );
}
